import React, { useEffect } from 'react';
import { resolveImage } from '../utils/image';
import { useNavigate } from 'react-router-dom';
import {
  FiX,
  FiShoppingCart,
  FiTrash2,
  FiPlus,
  FiMinus,
  FiShoppingBag,
} from 'react-icons/fi';
import { useCart } from '../contexts/CartContext';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  shippingFee?: number;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose, shippingFee = 0 }) => {
  const navigate = useNavigate();
  const { cart, cartSubtotal, cartIvaTotal, cartTotal, itemCount, updateQuantity, removeFromCart, clearCart } =
    useCart();

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
    }
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleCheckout = () => {
    onClose();
    navigate('/ecommerce/checkout');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 animate-fade-in"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 shadow-modal flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-pagali-gray-200">
          <div className="flex items-center gap-2">
            <FiShoppingCart className="text-xl text-pagali-purple" />
            <h2 className="text-lg font-semibold text-pagali-gray-800">
              Carrinho
            </h2>
            {itemCount > 0 && (
              <span className="bg-pagali-purple text-white text-xs font-medium px-2 py-0.5 rounded-full">
                {itemCount}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-pagali-gray-100 transition-colors"
          >
            <FiX className="text-xl text-pagali-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {cart.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center">
              <div className="w-20 h-20 rounded-full bg-pagali-gray-100 flex items-center justify-center mb-4">
                <FiShoppingBag className="text-3xl text-pagali-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-pagali-gray-800 mb-1">
                Carrinho vazio
              </h3>
              <p className="text-sm text-pagali-gray-500 mb-4">
                Adicione produtos ao carrinho para continuar
              </p>
              <button
                onClick={() => {
                  onClose();
                  navigate('/ecommerce');
                }}
                className="text-pagali-primary font-medium hover:underline"
              >
                Explorar lojas
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {cart.map((item) => {
                const imageSrc = resolveImage(item.IMAGEM, item.MIME_TYPE);
                const price = item.PRECO_PROMOCIONAL || item.PRECO;
                const subtotal = price * item.QTD;

                return (
                  <div
                    key={item.ID}
                    className="flex gap-3 p-3 bg-pagali-gray-50 rounded-xl"
                  >
                    {/* Product Image */}
                    <div className="w-16 h-16 rounded-lg bg-white overflow-hidden flex-shrink-0">
                      {imageSrc ? (
                        <img
                          src={imageSrc}
                          alt={item.NOME}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FiShoppingBag className="text-xl text-pagali-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-pagali-gray-800 text-sm truncate">
                        {item.NOME}
                      </h4>
                      <p className="text-xs text-pagali-gray-500 mt-0.5">
                        {price.toLocaleString('pt-CV')} ECV cada
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => updateQuantity(item.ID, item.QTD - 1)}
                            className="w-6 h-6 rounded-full bg-white border border-pagali-gray-200 flex items-center justify-center text-pagali-gray-600 hover:border-pagali-primary hover:text-pagali-primary transition-colors"
                          >
                            <FiMinus className="text-xs" />
                          </button>
                          <span className="w-8 text-center text-sm font-medium">
                            {item.QTD}
                          </span>
                          <button
                            onClick={() => updateQuantity(item.ID, item.QTD + 1)}
                            disabled={item.STOCK !== undefined && item.QTD >= item.STOCK}
                            className="w-6 h-6 rounded-full bg-white border border-pagali-gray-200 flex items-center justify-center text-pagali-gray-600 hover:border-pagali-primary hover:text-pagali-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <FiPlus className="text-xs" />
                          </button>
                        </div>

                        <span className="font-semibold text-pagali-gray-900">
                          {subtotal.toLocaleString('pt-CV')} ECV
                        </span>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => removeFromCart(item.ID)}
                      className="p-1.5 text-pagali-gray-400 hover:text-pagali-error transition-colors self-start"
                    >
                      <FiTrash2 className="text-sm" />
                    </button>
                  </div>
                );
              })}

            </div>
          )}
        </div>

        {/* Footer */}
        {cart.length > 0 && (
          <div className="border-t border-pagali-gray-200 p-4 space-y-4 bg-white">
            {/* Summary */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-pagali-gray-600">
                <span>Subtotal ({itemCount} {itemCount === 1 ? 'item' : 'items'})</span>
                <span>{Math.round(cartSubtotal).toLocaleString('pt-CV')} ECV</span>
              </div>
              {cartIvaTotal > 0 && (
                <div className="flex justify-between text-sm text-pagali-gray-600">
                  <span>IVA incluído</span>
                  <span>+ {Math.round(cartIvaTotal).toLocaleString('pt-CV')} ECV</span>
                </div>
              )}
              <div className="flex justify-between text-sm text-pagali-gray-500">
                <span>Taxa de entrega</span>
                {shippingFee > 0
                  ? <span>{Math.round(shippingFee).toLocaleString('pt-CV')} ECV</span>
                  : <span className="text-pagali-success">A calcular</span>
                }
              </div>
              <div className="flex justify-between text-lg font-semibold text-pagali-gray-900 pt-2 border-t border-pagali-gray-100">
                <span>Total{cartIvaTotal > 0 ? ' (c/ IVA)' : ''}</span>
                <span>{Math.round(cartTotal + shippingFee).toLocaleString('pt-CV')} ECV</span>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={handleCheckout}
                className="w-full py-3 px-4 bg-pagali-purple text-white font-medium rounded-xl hover:bg-pagali-purple-light transition-colors"
              >
                Finalizar Compra
              </button>
              <button
                onClick={onClose}
                className="w-full py-3 px-4 bg-pagali-gray-100 text-pagali-gray-700 font-medium rounded-xl hover:bg-pagali-gray-200 transition-colors"
              >
                Continuar Comprando
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Limpar o carrinho? Todos os produtos serão removidos.')) {
                    clearCart();
                    onClose();
                  }
                }}
                className="w-full py-2 text-sm text-pagali-error hover:text-red-700 transition-colors flex items-center justify-center gap-1.5"
              >
                <FiTrash2 className="text-sm" />
                Limpar carrinho
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Custom animation for slide-in-right */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slideInRight 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default CartDrawer;
